import React, { createContext, useEffect, useMemo, useState } from 'react';
import { useAppSelector } from '@/store';
import { 
  selectUser, 
  selectLeaseIds, 
  selectConnectionLeaseIds,
  selectHasMultipleLeases,
  selectHasEmptyState
} from '@/store/selectors';

interface PaymentSummaryContextValue {
  currentlyDisplayedLeaseId: number;
  multipleLeases: boolean;
  emptyState: boolean;
  setCurrentlyDisplayedLeaseId: (leaseId: number) => void;
}

const initialValues: PaymentSummaryContextValue = {
  currentlyDisplayedLeaseId: 0,
  multipleLeases: false,
  emptyState: false,
  setCurrentlyDisplayedLeaseId: () => {},
};

export const PaymentSummaryContext = createContext<PaymentSummaryContextValue>(initialValues);

export const PaymentSummaryProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  // Use memoized selectors for better performance
  const user = useAppSelector(selectUser);
  const ownLeaseIds = useAppSelector(selectLeaseIds);
  const connectionLeaseIds = useAppSelector(selectConnectionLeaseIds);
  const multipleLeases = useAppSelector(selectHasMultipleLeases);
  const emptyState = useAppSelector(selectHasEmptyState);

  const defaultLeaseId = ownLeaseIds[0] || connectionLeaseIds[0] || 0;
  const [selectedLeaseId, setSelectedLeaseId] = useState<number>(!multipleLeases && !emptyState ? defaultLeaseId : 0);

  const value = useMemo<PaymentSummaryContextValue>(() => ({
    currentlyDisplayedLeaseId: selectedLeaseId,
    multipleLeases,
    emptyState,
    setCurrentlyDisplayedLeaseId: setSelectedLeaseId,
  }), [selectedLeaseId, multipleLeases, emptyState]);

  // When linked accounts load after initial render, auto-select a default lease
  useEffect(() => {
    if (selectedLeaseId === 0 && !multipleLeases && !emptyState && defaultLeaseId) {
      setSelectedLeaseId(defaultLeaseId);
    }
  }, [selectedLeaseId, multipleLeases, emptyState, defaultLeaseId]);

  return (
    <PaymentSummaryContext.Provider value={value}>{children}</PaymentSummaryContext.Provider>
  );
};


